import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SubscriptionData {
  isActive: boolean;
  source: 'manual_override' | 'stripe' | 'no_customer' | 'no_active_subscription';
  tier: 'basic' | 'premium' | 'enterprise' | null;
  subscription: {
    id: string;
    status: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
  } | null;
}

export const useSubscription = () => {
  const { data: settings, isLoading: settingsLoading, error: settingsError } = useQuery({
    queryKey: ['subscription-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_settings')
        .select('*')
        .order('created_at', { ascending: true })
        .limit(1)
        .single();
      
      if (error) throw error;
      return data;
    },
    refetchInterval: 30000,
    staleTime: 10000,
  });

  // Site-wide subscription check - no authentication required
  const { data: subscriptionData, isLoading: subscriptionLoading, refetch: refetchSubscription } = useQuery<SubscriptionData>({
    queryKey: ['subscription-status'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-subscription-status');
      if (error) throw error;
      return data as SubscriptionData;
    },
    refetchInterval: 60000,
    staleTime: 30000,
    enabled: !!settings,
  });

  // Check if subscription is disabled at the site level
  const subscriptionDisabled = settings?.subscription_disabled ?? false;
  
  // If subscription is disabled site-wide, grant access to all features
  const isActive = subscriptionDisabled ? false : (subscriptionData?.isActive ?? false);
  const tier = subscriptionDisabled ? null : (subscriptionData?.tier ?? null);
  const isLoading = settingsLoading || subscriptionLoading;

  return {
    isActive,
    tier,
    isLoading,
    settings,
    subscriptionData,
    error: settingsError,
    refetchSubscription,
  };
};
